import React, { useEffect, useMemo, useState } from "react";
import ReactSelect from "react-select";
import _ from "lodash";

import moment from "moment";

import { Button, ButtonVariant } from "@src/components/button/Button";
import { Input } from "@src/components/input/Input";
import { Loader } from "@src/components/loader/Loader";
import { VGrid, IColumn } from "@src/components/vGrid/VGrid";
import { DateFormatsEnum, formatDate } from "@src/components/utils/dateUtils";
import { filtersConfig, OptionType } from "@src/components/utils/reactSelectUtils";
import { systemActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { IItemHistory } from "@src/store/system/ViewHistory";
import { Theme } from "@src/store/settingsSlice";
import DownloadIcon from "@src/assets/general-icons/DownloadIcon";

import styles from "./ViewHistory.module.scss";

const TIME_WITH_SECONDS = "hh:mm:ss A";
const formatTime = (x: Date | string | undefined | null) => (x ? moment(x).format(TIME_WITH_SECONDS) : "--");

export interface IViewHistoryListingDetails {
	listingId?: number;
	eventName?: string;
	date?: string;
	time?: string;
	venue?: string;
	section?: string;
	row?: string;
	seats?: string;
}

interface IViewHistoryProps {
	itemIds: Array<number>;
	listingDetails: IViewHistoryListingDetails;
	onClose?: () => void;
}

const FILTER_OPTIONS: Array<OptionType> = [
	{ label: "All", value: "__ALL__" },
	{ label: "Pricing", value: "Pricing" },
	{ label: "Barcode", value: "Barcode" },
	{ label: "PDF", value: "PDF" },
	{ label: "Sharing", value: "Sharing" },
	{ label: "Notes", value: "Notes" },
	{ label: "Tags", value: "Tags" },
	{ label: "Hold", value: "Hold" },
	{ label: "Invoice", value: "Invoice" },
	{ label: "Properties", value: "Properties" },
	{ label: "Location", value: "Location" },
	{ label: "Purchase Cost", value: "Purchase" },
];

const historyColumns: Array<IColumn<IItemHistory>> = [
	{
		id: "Date",
		header: "Date",
		accessor: "CreatedDate",
		formatValue: (x) => formatDate(x, DateFormatsEnum["MM/DD/YYYY"]),
		hasTooltip: true,
		width: 110,
	},
	{
		id: "Time",
		header: "Time",
		accessor: "CreatedDate",
		formatValue: (x) => formatTime(x),
		hasTooltip: true,
		width: 130,
	},
	{
		id: "User",
		header: "User",
		accessor: "UserName",
		width: 180,
		hasTooltip: true,
	},
	{
		id: "Section",
		header: "Section",
		accessor: "Section",
		width: 80,
	},
	{
		id: "Row",
		header: "Row",
		accessor: "Row",
		width: 60,
	},
	{
		id: "Seat",
		header: "Seat",
		accessor: "Seat",
		width: 60,
	},
	{
		id: "Activity",
		header: "Activity",
		accessor: "Activity",
		hasTooltip: true,
		width: 360,
		minWidth: 240,
	},
];

const buildExportCsv = (rows: Array<IItemHistory>): string => {
	const header = ["Date", "Time", "User", "Section", "Row", "Seat", "Activity"];
	const escape = (v: string | number) => {
		const s = String(v ?? "");
		return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
	};
	const lines = rows.map((r) =>
		[
			formatDate(r.CreatedDate, DateFormatsEnum["MM/DD/YYYY"]),
			formatTime(r.CreatedDate),
			r.UserName,
			r.Section,
			r.Row,
			r.Seat,
			r.Activity,
		]
			.map(escape)
			.join(","),
	);
	return [header.join(","), ...lines].join("\n");
};

export const ViewHistory: React.FC<IViewHistoryProps> = ({ itemIds, listingDetails, onClose }) => {
	const dispatch = useAppDispatch();
	const { data, loading, error } = useAppSelector((state) => state.system.viewItemHistory);
	const theme = useAppSelector((state) => state.settings.theme) as Theme;

	const [searchTerm, setSearchTerm] = useState("");
	const [selectedFilter, setSelectedFilter] = useState<string>("__ALL__");

	useEffect(() => {
		if (!itemIds.length) return;
		dispatch(systemActions.viewItemHistory({ itemIds, skipCache: true, skipSavingCache: true }));
	}, [dispatch, itemIds]);

	const filteredRows = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		const allSelected = selectedFilter === "__ALL__";
		const filtered = data.filter((row) => {
			if (term) {
				const haystack = `${row.Activity ?? ""} ${row.UserName ?? ""}`.toLowerCase();
				if (!haystack.includes(term)) return false;
			}
			if (!allSelected && row.ActivityType !== selectedFilter) return false;
			return true;
		});
		return _.orderBy(filtered, (r) => new Date(r.CreatedDate).getTime(), "desc");
	}, [data, searchTerm, selectedFilter]);

	const detailItems: Array<{ label: string; value?: string | number }> = [
		{ label: "Listing ID", value: listingDetails.listingId },
		{ label: "Event", value: listingDetails.eventName },
		{ label: "Date", value: listingDetails.date },
		{ label: "Time", value: listingDetails.time },
		{ label: "Venue", value: listingDetails.venue },
		{ label: "Section", value: listingDetails.section },
		{ label: "Row", value: listingDetails.row },
		{ label: "Seats", value: listingDetails.seats },
	];

	const handleExport = () => {
		const csv = buildExportCsv(filteredRows);
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `listing-${listingDetails.listingId ?? "history"}-history.csv`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	const selectedOption = FILTER_OPTIONS.find((o) => o.value === selectedFilter) ?? FILTER_OPTIONS[0];

	const renderBody = () => {
		if (loading) {
			return (
				<div className={styles.stateContainer}>
					<Loader />
				</div>
			);
		}

		if (error) {
			return <div className={`${styles.stateContainer} ${styles.errorText}`}>{error}</div>;
		}

		if (!filteredRows.length) {
			return <div className={styles.stateContainer}>No history</div>;
		}

		return (
			<div className={styles.tableWrap}>
				<VGrid<IItemHistory>
					tableId="view-history"
					getRowNodeId={(row) => `${row.ItemId}-${row.CreatedDate}-${row.ActivityType}-${row.Activity}`}
					columns={historyColumns}
					rowsData={filteredRows}
				/>
			</div>
		);
	};

	return (
		<>
			<div className={styles.root}>
				<div className={styles.left}>
					<div className={styles.detailBlock}>
						<div className={styles.detailLabel}>Selected Listing</div>
					</div>
					{detailItems.map(({ label, value }) =>
						value !== undefined && value !== null && value !== "" ? (
							<div key={label} className={styles.detailBlock}>
								<div className={styles.detailLabel}>{label}</div>
								<div className={styles.detailValue}>{value}</div>
							</div>
						) : null,
					)}
				</div>
				<div className={styles.right}>
					<div className={styles.toolbar}>
						<div className={styles.searchWrapper}>
							<Input
								disableXbutton
								name="viewHistorySearch"
								value={searchTerm}
								placeholder="Search for logs"
								inputContainerClassName={styles.searchInputContainer}
								onChange={(e) => setSearchTerm(e.target.value)}
							/>
						</div>
						<div className={styles.filterWrapper}>
							<div className={styles.filterLabel}>Filter by:</div>
							<div className={styles.filterSelect}>
								<ReactSelect<OptionType>
									options={FILTER_OPTIONS}
									value={selectedOption}
									onChange={(opt) => {
										const single = opt as OptionType | null;
										setSelectedFilter(single ? String(single.value) : "__ALL__");
									}}
									isSearchable={false}
									placeholder="All"
									styles={(() => {
										const base = filtersConfig("", theme);
										return {
											...base,
											control: (provided, props) => ({
												...(base.control?.(provided, props) ?? provided),
												minHeight: "38px",
											}),
											container: (provided, props) => ({
												...(base.container?.(provided, props) ?? provided),
												flex: "0 0 auto",
											}),
										};
									})()}
									components={{ IndicatorSeparator: null }}
								/>
							</div>
						</div>
					</div>
					{renderBody()}
				</div>
			</div>
			<div className={styles.footer}>
				<Button onClick={handleExport} variant={ButtonVariant.Secondary} disabled={loading || !filteredRows.length}>
					<DownloadIcon />
					Export
				</Button>
				<Button onClick={onClose} variant={ButtonVariant.Primary}>
					Done
				</Button>
			</div>
		</>
	);
};

export default ViewHistory;
